/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable camelcase */
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/core';

import BulmaButton from 'react-bulma-components/lib/components/button';
import BulmaHero from 'react-bulma-components/lib/components/hero';
import BulmaSection from 'react-bulma-components/lib/components/section';
import BulmaCard from 'react-bulma-components/lib/components/card';

import { RichText } from 'prismic-reactjs';
import { linkResolver } from '../../util/linkResolver';

import { createRandomId } from '../../util/createRandomId';

import { Button, Heading, Modal, WistiaVideo, LazyLink, LazyImage } from '../atoms';

import {
  StyledBackgroundVideo,
  StyledVideoContainer,
  styledVideoHero,
  styledVideoHeroSection,
  styledVideoHeroFullscreen,
  styledButtonContainer,
  StyledVideoPlay,
  styledModal,
  styledHeroBox,
  styledHeroBoxTitle,
  StyledForm,
  styledSubmit,
  StyledSubscript,
  StyledButtonsContainer,
  VideoContentFormWrapper,
  videoContentContainer,
  VideoFormContainer,
  videoContentBusinessContainer,
} from './HeroVideoStyles';

const renderHeading = (text, atts) => <Heading {...atts}>{text.text}</Heading>;

const renderButton = (link, text, atts) => (
  <LazyLink {...atts} url={link.url}>
    {text.text}
  </LazyLink>
);

const HeroVideo = ({ input }) => {
  const {
    fullscreen,
    large_text,
    text_color,
    heading,
    heading_color,
    subheading,
    subheading_color,
    cta_text_primary,
    cta_url_primary,
    cta_text_secondary,
    cta_url_secondary,
    hero_video_url,
    hero_video_placeholder_img,
    video_icon,
    video_timestamp,
    video_timestamp_color,
    video_title,
    video_title_color,
    video_wistia_id,
    hero_video_color_overlay,
    email_placeholder,
    form_title,
    name_placeholder,
    password_placeholder,
    submit_button_text,
    subscript,
    form_id,
    submit_button_id,
  } = input.primary;

  const { fields } = input;

  const [modalIsShowing, setModalIsShowing] = useState(false);

  const renderVideoModal = wistiaId => (
    <Modal show={modalIsShowing} css={styledModal} onClose={() => setModalIsShowing(false)}>
      <WistiaVideo wistiaId={wistiaId} autoplay />
    </Modal>
  );

  const onFormSubmit = event => {
    event.preventDefault();

    const getFormId = event.currentTarget.id;

    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_button',
      tags: {
        action: 'click',
        category: 'form',
        name: 'signup',
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: 'form',
      name: 'signup',
    });

    if (document.getElementById(getFormId)) {
      setTimeout(function() {
        document.getElementById(getFormId).submit();
      }, 500);
    }
  };

  const onButtonClick = event => {
    const clickId = event.currentTarget.id.replace('_button', '').replace('_biz', '');
    const googleIdCheck = clickId === 'Sign up with Google' ? 'google' : clickId;
    const dataHref = event.currentTarget.dataset.href;
    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_button',
      tags: {
        action: 'click',
        category: googleIdCheck,
        name: 'signup',
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: googleIdCheck,
      name: 'signup',
    });

    if (dataHref) {
      setTimeout(function() {
        window.location.href = dataHref;
      }, 500);
    }
  };

  return (
    <BulmaSection
      css={css`
        ${styledVideoHeroSection}
        ${fullscreen && styledVideoHeroFullscreen}
      `}
    >
      <BulmaHero css={styledVideoHero}>
        <BulmaHero.Body>
          <VideoContentFormWrapper>
            <div css={form_title ? videoContentContainer : videoContentBusinessContainer}>
              {renderHeading(heading[0], {
                style: heading_color ? { color: heading_color } : null,
                className: `${text_color === 'light' ? 'h1 white' : 'h1 darkblue'} ${large_text ? 'hero-title' : ''}`,
              })}
              {renderHeading(subheading[0], {
                style: subheading_color ? { color: subheading_color } : null,
                className: text_color === 'light' ? 'large white' : 'large darkgrey',
                subtitle: true,
                renderAs: 'p',
              })}
              <BulmaButton.Group css={styledButtonContainer}>
                {cta_url_primary
                  ? renderButton(cta_url_primary, cta_text_primary[0], { className: 'button-link is-white' })
                  : null}
                {cta_url_secondary
                  ? renderButton(cta_url_secondary, cta_text_secondary[0], {
                      className:
                        text_color === 'light'
                          ? 'button-link is-white is-outlined'
                          : 'button-link is-white is-outlined',
                    })
                  : null}
              </BulmaButton.Group>
              <StyledVideoPlay
                role="button"
                tabIndex="0"
                onClick={() => setModalIsShowing(true)}
                onKeyPress={() => setModalIsShowing(true)}
                id="testVideoPlayButton"
              >
                <LazyImage src={video_icon.url} width={20} bulma />
                <p style={{ color: video_title_color || (text_color === 'light' ? 'white' : '#5b6987') }}>
                  {video_title[0].text}
                </p>
                <p
                  style={{
                    color: video_timestamp_color || (text_color === 'light' ? 'white' : '#5b6987'),
                  }}
                >
                  {video_timestamp[0].text}
                </p>
              </StyledVideoPlay>
            </div>
            {form_title ? (
              <VideoFormContainer>
                <div className="videoFormWrapper">
                  <BulmaCard css={styledHeroBox} data-testid="hero_box">
                    <BulmaCard.Header.Title css={styledHeroBoxTitle}>
                      {renderHeading(form_title[0], {
                        className: 'h5 darkblue',
                        renderAs: 'h5',
                        'data-testid': 'hero_box_title',
                      })}
                    </BulmaCard.Header.Title>
                    <BulmaCard.Content>
                      <StyledForm
                        className="form-align-left"
                        action="https://cloud.digitalocean.com/registrations"
                        method="post"
                        id={form_id || 'defaultId'}
                        onSubmit={onFormSubmit}
                      >
                        <span className="form-wrapper">
                          <input
                            aria-label="signup button"
                            type="text"
                            minLength="2"
                            name="user[name]"
                            placeholder="First Name"
                            required
                          />
                          <input
                            aria-label="email"
                            type="email"
                            name="user[email]"
                            placeholder="Email Address"
                            required
                          />
                          <input
                            aria-label="password"
                            type="password"
                            minLength="8"
                            name="user[password]"
                            placeholder="Password"
                            required
                          />
                          <Button
                            className="is-primary"
                            css={styledSubmit}
                            type="submit"
                            onClick={onButtonClick}
                            id={submit_button_id || 'deafultId'}
                          >
                            {submit_button_text}
                          </Button>
                        </span>
                      </StyledForm>
                    </BulmaCard.Content>
                    <StyledButtonsContainer>
                      {fields.map(button => (
                        <Button
                          className={`is-primary is-outlined is-small
                            ${button.button_text !== null ? 'has-left-img' : 'has-center-img'}`}
                          key={createRandomId()}
                          onClick={onButtonClick}
                          data-href={button.button_link.url}
                          id={button.button_text || 'github_button'}
                          aria-label={button.button_text || 'signup_button'}
                        >
                          <LazyImage src={button.icon.url} alt={button.icon.alt || button.button_text || 'icon'} />
                          {button.button_text}
                        </Button>
                      ))}
                    </StyledButtonsContainer>
                  </BulmaCard>
                  <StyledSubscript>
                    <RichText render={subscript} linkResolver={linkResolver} />
                  </StyledSubscript>
                </div>
              </VideoFormContainer>
            ) : null}
          </VideoContentFormWrapper>
        </BulmaHero.Body>
      </BulmaHero>
      <StyledVideoContainer
        css={{
          background: `${hero_video_color_overlay}`,
        }}
      />
      <StyledBackgroundVideo
        src={hero_video_url && hero_video_url.url ? hero_video_url.url : null}
        autoPlay="autoplay"
        loop="loop"
        muted="muted"
        poster={hero_video_placeholder_img && hero_video_placeholder_img.url ? hero_video_placeholder_img.url : null}
      />
      {renderVideoModal(video_wistia_id)}
    </BulmaSection>
  );
};

export default HeroVideo;

HeroVideo.propTypes = {
  input: PropTypes.object.isRequired,
};
